import { LoggableAPI } from '../models/protocols/loggable-api';
import { inject, Injectable } from '@angular/core';
import { ApiClient } from './api-client';
import { ODataQueryOptions } from '../models/shared/odata-query-options';
import { Observable, throwError } from 'rxjs';
import { ODataResponse } from '../models/protocols/odata-response';
import { Endpoints } from './endpoints';
import { catchError, map } from 'rxjs/operators';
import { CustomError } from '../models/shared/custom-error';
import { EmployerUser } from '../models/users/dto/employer-user';
import { EmployerMemberRecord } from '../models/users/dto/employer-member-record';
import { CreateEmployerUserRequest } from '../models/users/requests/create-employer-user-request';
import { Role } from '../models/roles/role';
import { ChangeEmailRequest } from '../models/users/requests/change-email-request';
import { HttpResponse } from '@angular/common/http';
import { MemberUser } from '../models/users/dto/member-user';
import { CreateMemberRequest } from '../models/users/requests/create-member-request';
import { EmployerSearchResult } from '../models/shared/employer-search-result';
import { EpSelfEnrolmentRequest } from '../views/auth/components/auth/ep-self-enrolment/requests/ep-self-enrolment.request';
import { EpSelfEnrolmentResponse } from '../models/employers/ep-self-enrolment-response';
import { Employer } from '../models/employers/employer';
import { IpUpdateEmployerReq } from '../models/employers/requests/ip-update-employer-req';
import { IpEmployerSubmission } from '../models/submissions/employer-submissions/ip-employer-submission';
import { EmployerUserRecord } from '../models/users/dto/employer-user-record';
import { IpUpdateEmployerUserReq } from '../models/employers/requests/ip-update-employer-user-req';
import { EpUpdateEmployerUserReq } from '../models/employers/requests/ep-update-employer-user-req';
import { IpUpdateEmployerContactsRequest } from '../models/users/requests/submissionRequests/ip-update-employer-contacts-request';
import { IpCreateChangeEmployerAddressSubmissionReq } from '../models/employers/requests/ip-create-change-employer-address-submission-req';
import { EpCreateChangeEmployerAddressSubmissionReq } from '../models/employers/requests/ep-create-change-employer-address-submission-req';
import { EpCreateUpdateEmploymentStatusSubmissionReq } from '../models/employers/requests/ep-create-update-employment-status-submission-req';
import { Mentionable } from '../models/base/mentionable';
import { IpEmployerRecordComment } from '../models/employers/ip-employer-record-comment';
import { IpEmployerRecordCommentRequest } from '../models/employers/requests/ip-employer-record-comment-request';
import { EpUpdateEmployerContactsReq } from '../models/employers/requests/ep-update-employer-contacts-req';
import { EpEmployerSubmission } from '../models/submissions/employer-submissions/ep-employer-submission';
import { IpCreateUpdateEmploymentStatusSubmissionReq } from '../models/employers/requests/ip-create-update-employment-status-submission-req';

@Injectable({
  providedIn: 'root'
})
export class EmployersAPI implements LoggableAPI {
  constructor() {}

  private apiClient = inject(ApiClient);

  public serviceName = 'Employers';

  public epSelfEnrol(req: EpSelfEnrolmentRequest): Observable<EpSelfEnrolmentResponse> {
    const url = Endpoints.epSelfEnrol();
    return this.apiClient.postObj(EpSelfEnrolmentResponse, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetEmployerUserRoles(opts: ODataQueryOptions): Observable<ODataResponse<Role>> {
    const url = Endpoints.epGetUserRoles();
    return this.apiClient.getOdata(url, Role, opts).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetEmployerUsers(opts: ODataQueryOptions): Observable<ODataResponse<EmployerUserRecord>> {
    const url = Endpoints.epGetUsersForEmployer();
    return this.apiClient.getOdata<EmployerUserRecord>(url, EmployerUserRecord, opts).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetEmployerUserById(userId: number, odataQueryOptions: ODataQueryOptions): Observable<EmployerUserRecord> {
    const url = Endpoints.epGetEmployerUserById(userId);
    return this.apiClient.getOdataObj<EmployerUserRecord>(url, EmployerUserRecord, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epUpdateEmployerUser(req: EpUpdateEmployerUserReq): Observable<EmployerUserRecord> {
    const url = Endpoints.epUpdateEmployerUser(req?.id);
    return this.apiClient.putObj(EmployerUserRecord, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epCreateEmployerUser(req: CreateEmployerUserRequest): Observable<EmployerUser> {
    const url = Endpoints.epCreateEmployerUser();
    return this.apiClient.postObj(EmployerUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateEmployerUser(req: CreateEmployerUserRequest): Observable<EmployerUser> {
    const url = Endpoints.ipCreateEmployerUser(req.employerId);
    return this.apiClient.postObj(EmployerUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createEmployee(req: CreateMemberRequest): Observable<MemberUser> {
    const url = Endpoints.createEmployee();
    return this.apiClient.postObj(MemberUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetUserRoles(odataQueryOptions: ODataQueryOptions): Observable<ODataResponse<Role>> {
    const url = Endpoints.epGetUserRoles();
    const permissionHeaders = { permissions: [3, 9] };
    return this.apiClient.getOdata(url, Role, odataQueryOptions, permissionHeaders).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerUserById(
    employerId: number,
    userId: number,
    odataQueryOptions: ODataQueryOptions
  ): Observable<EmployerUserRecord> {
    const url = Endpoints.ipGetEmployerUserById(employerId, userId);
    return this.apiClient.getOdataObj<EmployerUserRecord>(url, EmployerUserRecord, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerById(employerId: number, odataQueryOptions?: ODataQueryOptions): Observable<Employer> {
    const url = Endpoints.ipGetEmployerById(employerId);
    return this.apiClient.getOdataObj<Employer>(url, Employer, odataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateEmployerUser(req: IpUpdateEmployerUserReq): Observable<EmployerUserRecord> {
    const url = Endpoints.ipUpdateEmployerUser(req?.employerId, req?.id);
    return this.apiClient.putObj(EmployerUserRecord, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipForceChangeEmployerUserEmail(
    employerId: number,
    employerUserId: number,
    req: ChangeEmailRequest
  ): Observable<EmployerUserRecord> {
    const url = Endpoints.ipForceChangeEmployerUserEmail(employerId, employerUserId);
    return this.apiClient.postObj(EmployerUserRecord, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipForceChangeEmployerUserPassword(employerId: number, employerUserId: number): Observable<HttpResponse<any>> {
    const url = Endpoints.ipForceChangeEmployerUserPassword(employerId, employerUserId);
    return this.apiClient.simpleBodylessPost(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public forceChangeEmail(u: EmployerUser, req: ChangeEmailRequest): Observable<EmployerUser> {
    const url = Endpoints.employerForceChangeEmail(u);
    return this.apiClient.postObj(EmployerUser, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  // This endpoint works to both force reset another Employer User's password, and resend the welcome email.
  public forceResetPassword(u: EmployerUser): Observable<HttpResponse<any>> {
    const url = Endpoints.employerForceChangePassword(u);
    return this.apiClient.simpleBodylessPost(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public searchEmployersByIdOrName(
    odataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<EmployerSearchResult>> {
    const url = Endpoints.searchEmployersByIdOrName();
    return this.apiClient.getOdata<EmployerSearchResult>(url, EmployerSearchResult, odataQueryOptions, null).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployers(opts: ODataQueryOptions): Observable<ODataResponse<Employer>> {
    const url = Endpoints.ipGetEmployers();
    return this.apiClient.getOdata<Employer>(url, Employer, opts).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateEmployer(req: IpUpdateEmployerReq): Observable<Employer> {
    const url = Endpoints.ipUpdateEmployer(req?.id);
    return this.apiClient.putObj(Employer, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateUpdateEmployerAddressSubmission(
    req: IpCreateChangeEmployerAddressSubmissionReq
  ): Observable<IpEmployerSubmission> {
    const url = Endpoints.ipCreateUpdateEmployerAddressSubmission();
    return this.apiClient.postObj(IpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epCreateUpdateEmployerAddressSubmission(
    req: EpCreateChangeEmployerAddressSubmissionReq
  ): Observable<EpEmployerSubmission> {
    const url = Endpoints.epCreateUpdateEmployerAddressSubmission();
    return this.apiClient.postObj(EpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateUpdateEmploymentStatusSubmission(
    req: IpCreateUpdateEmploymentStatusSubmissionReq
  ): Observable<IpEmployerSubmission> {
    const url = Endpoints.ipCreateUpdateEmploymentStatusSubmission();
    return this.apiClient.postObj(IpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epCreateUpdateEmploymentStatusSubmission(
    req: EpCreateUpdateEmploymentStatusSubmissionReq
  ): Observable<EpEmployerSubmission> {
    const url = Endpoints.epCreateUpdateEmploymentStatusSubmission();
    return this.apiClient.postObj(EpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetUsersForEmployer(
    opts: ODataQueryOptions,
    employerId: number
  ): Observable<ODataResponse<EmployerUserRecord>> {
    const url = Endpoints.ipGetUsersForEmployer(employerId);
    return this.apiClient.getOdata<EmployerUserRecord>(url, EmployerUserRecord, opts).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerMembers(
    oDataQueryOptions: ODataQueryOptions,
    employerId: number
  ): Observable<ODataResponse<EmployerMemberRecord>> {
    const url = Endpoints.ipGetEmployerMembers(employerId);
    oDataQueryOptions.setExpand('EmploymentStatus');
    return this.apiClient.getOdata<EmployerMemberRecord>(url, EmployerMemberRecord, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerUserRoles(opts?: ODataQueryOptions): Observable<ODataResponse<Role>> {
    const url = Endpoints.ipGetEmployerUserRoles();
    return this.apiClient.getOdata<Role>(url, Role, opts).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateEmployerContacts(req: IpUpdateEmployerContactsRequest): Observable<IpEmployerSubmission> {
    const url = Endpoints.ipUpdateEmployerContacts();
    return this.apiClient.postObj(IpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epUpdateEmployerContacts(req: EpUpdateEmployerContactsReq): Observable<EpEmployerSubmission> {
    const url = Endpoints.epUpdateEmployerContacts();
    return this.apiClient.postObj(EpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerCommentMentionables(opts: ODataQueryOptions): Observable<ODataResponse<Mentionable>> {
    const url = Endpoints.ipGetEmployerCommentMentionables();
    return this.apiClient.getOdata<Mentionable>(url, Mentionable, opts).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetCommentsForEmployer(employerId: string): Observable<ODataResponse<IpEmployerRecordComment>> {
    const url = Endpoints.ipGetCommentsForEmployer(employerId);
    return this.apiClient.getOdata<IpEmployerRecordComment>(url, IpEmployerRecordComment, undefined).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipAddEmployerRecordComment(req: IpEmployerRecordCommentRequest): Observable<IpEmployerRecordComment[]> {
    const url = Endpoints.ipAddEmployerRecordComment(req.employerId);
    return this.apiClient.simplePost(url, req).pipe(
      map(res => {
        const comments = window?.injector?.Deserialize?.arrayOf(IpEmployerRecordComment, res.body);
        return comments.sort((a: IpEmployerRecordComment, b: IpEmployerRecordComment) => {
          return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
        });
      }),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetEmployer(oDataQueryOptions: ODataQueryOptions): Observable<Employer> {
    const url = Endpoints.epGetEmployer();
    return this.apiClient.getOdataObj<Employer>(url, Employer, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }
}
